import {useState, useRef, useEffect, useCallback} from 'react';
import * as DentalAiApi from '../api/DentalAiApi';
import PageShell from '../components/PageShell';
import './DentalAi.css';
import ChoiceRow from '../components/ChoiceRow';
import useIdempotencyKeys from '../hooks/useIdempotencyKeys';
import {
  GONE_MESSAGE,
  IMAGE_MESSAGE,
  STILL_PROCESSING_MESSAGE,
  jobFailureMessage,
  pollUntilSettled,
  requestFailureMessage,
} from '../api/dentalAiJob';

const DETECTION_TYPES = [
  {
    id: 'caries',
    value: 'caries',
    label: 'Caries',
    description: 'Early signs of decay. Works on a close-up photo or a bitewing.',
  },
  {
    id: 'wisdom_teeth',
    value: 'wisdom_teeth',
    label: 'Wisdom teeth',
    description: 'Position and crowding. Needs a panoramic X-ray.',
  },
];

// One Idempotency-Key per detection: a retry of the same submit replays the same job.
const ACTION = 'dental-ai';

/** Natural size of the image, so the result overlay can be drawn on it. */
const measureImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight});
    img.onerror = () => reject(new Error(IMAGE_MESSAGE));
    img.src = src;
  });

/** Builds the view model from a succeeded job's result (the shape the sync endpoint returned). */
const toResult = (result, type, imageUrl, {naturalWidth, naturalHeight}) => {
  const boxes = ((result && result.detection_selection) || []).map((rect, idx) => ({
    id: String(idx),
    x: rect.xmin / naturalWidth,
    y: rect.ymin / naturalHeight,
    w: (rect.xmax - rect.xmin) / naturalWidth,
    h: (rect.ymax - rect.ymin) / naturalHeight,
    label: rect.crop_prediction,
    confidence: 85, // Mock confidence as the original API doesn't seem to provide it
  }));

  return {
    type,
    boxes,
    summary: boxes.length > 0 ? `${boxes.length} areas worth a look` : 'Nothing stood out',
    imageUrl,
    naturalWidth,
    naturalHeight,
  };
};

export default function DentalAi() {
  const [userDetectionType, setUserDetectionType] = useState('caries');
  const [userFile, setUserFile] = useState(null);
  const [fileBase64, setFileBase64] = useState(null);

  const [userResult, setUserResult] = useState(null);
  // idle | submitting | pending | gave-up | failed. `message` is set for failed.
  const [phase, setPhase] = useState({name: 'idle'});

  const [typeError, setTypeError] = useState(null);
  const [fileError, setFileError] = useState(null);

  const fileInputRef = useRef(null);
  const controllerRef = useRef(null);
  const keys = useIdempotencyKeys();

  /** Stops the current submit or poll. Its answers are ignored from then on. */
  const stopFollowing = useCallback(() => {
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = null;
  }, []);

  // Leaving the page (unmount) stops polling: no request is sent after this.
  useEffect(() => stopFollowing, [stopFollowing]);

  /** Polls one job until it settles, then shows its result or failure. */
  const follow = useCallback(async (controller, jobId, {type, imageUrl}) => {
    setPhase({name: 'pending'});
    let job;
    try {
      job = await pollUntilSettled({
        fetchStatus: (signal) => DentalAiApi.getDentalAiJob(jobId, {signal}),
        signal: controller.signal,
      });
    } catch {
      if (controller.signal.aborted) return;
      // 404: the job is unknown, expired, or not this account's.
      controllerRef.current = null;
      setPhase({name: 'failed', message: GONE_MESSAGE});
      return;
    }
    if (controller.signal.aborted) return;
    if (job.status === 'timed-out') {
      setPhase({name: 'gave-up', jobId, type, imageUrl});
      return;
    }
    if (job.status === 'failed') {
      controllerRef.current = null;
      setPhase({name: 'failed', message: jobFailureMessage(job.problem)});
      return;
    }
    try {
      const size = await measureImage(imageUrl);
      if (controller.signal.aborted) return;
      controllerRef.current = null;
      setUserResult(toResult(job.result, type, imageUrl, size));
      setPhase({name: 'idle'});
    } catch {
      if (controller.signal.aborted) return;
      controllerRef.current = null;
      setPhase({name: 'failed', message: IMAGE_MESSAGE});
    }
  }, []);

  /** Starts a new detection (a new key unless the last attempt left one to replay). */
  const start = () => {
    stopFollowing();
    const controller = new AbortController();
    controllerRef.current = controller;
    const type = userDetectionType;
    const imageUrl = fileBase64;

    setUserResult(null);
    setPhase({name: 'submitting'});

    keys
      .run(ACTION, {model: type, image: imageUrl}, (key) =>
        DentalAiApi.startDentalAi({dental_ai: type, image_url: imageUrl}, key, {signal: controller.signal}),
      )
      .then(
        (accepted) => follow(controller, accepted.job_id, {type, imageUrl}),
        (err) => {
          if (controller.signal.aborted) return;
          console.error('Dental AI detection failed:', err);
          controllerRef.current = null;
          setPhase({name: 'failed', message: requestFailureMessage(err)});
        },
      );
  };

  /** Resumes polling the same job after the wait gave up. No new job, so no new key. */
  const checkAgain = (jobId, type, imageUrl) => {
    stopFollowing();
    const controller = new AbortController();
    controllerRef.current = controller;
    follow(controller, jobId, {type, imageUrl});
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setFileError('File size exceeds 5 MB. Please upload smaller file');
        return;
      }
      setFileError(null);
      setUserFile(file);

      const reader = new FileReader();
      reader.onload = (event) => {
        setFileBase64(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTypeError(null);
    setFileError(null);

    let hasError = false;
    if (!userDetectionType) {
      setTypeError('Choose a detection type');
      hasError = true;
    }
    if (!fileBase64) {
      setFileError('Choose a photo or X-ray');
      hasError = true;
    }

    if (hasError) return;

    start();
  };

  const handleTryAnother = () => {
    stopFollowing();
    setUserResult(null);
    setUserFile(null);
    setFileBase64(null);
    setPhase({name: 'idle'});
    setTypeError(null);
    setFileError(null);
  };

  const busy = phase.name === 'submitting' || phase.name === 'pending' || phase.name === 'gave-up';
  const showResult = userResult && phase.name === 'idle';

  return (
    <PageShell title="Dental AI" showBackButton={true}>
      {!showResult ? (
        <form onSubmit={handleSubmit} className="dental-ai-container">
          <p className="dental-ai-subtitle">Upload a photo or X-ray and we&apos;ll take a first look.</p>

          <div className="dental-ai-card">
            <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>
              <fieldset className="choice-group-fieldset">
                <legend className="choice-group-legend">Detection type</legend>
                {typeError && (
                  <div role="alert" style={{color: '#C43B71', fontSize: '12px', marginBottom: '8px'}}>
                    {typeError}
                  </div>
                )}

                <div role="radiogroup" aria-label="Detection type">
                  {DETECTION_TYPES.map((type) => (
                    <ChoiceRow
                      key={type.id}
                      role="radio"
                      checked={userDetectionType === type.value}
                      onToggle={() => setUserDetectionType(type.value)}
                      label={type.label}
                      description={type.description}
                    />
                  ))}
                </div>
              </fieldset>

              <div>
                <input
                  type="file"
                  accept="image/*"
                  aria-label="Photo or X-ray"
                  ref={fileInputRef}
                  style={{display: 'none'}}
                  onChange={handleFileChange}
                />
                {fileError && (
                  <div role="alert" style={{color: '#C43B71', fontSize: '12px', marginBottom: '8px'}}>
                    {fileError}
                  </div>
                )}
                <div
                  className={`file-drop-zone ${userFile ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current.click()}
                  style={fileBase64 ? {padding: 0, overflow: 'hidden'} : {}}
                >
                  {fileBase64 ? (
                    <img
                      src={fileBase64}
                      alt="Preview"
                      style={{width: '100%', height: '100%', objectFit: 'contain', maxHeight: '200px'}}
                    />
                  ) : (
                    <>
                      <div
                        style={{
                          fontFamily: 'var(--font-inter), Inter, system-ui',
                          fontWeight: 600,
                          color: '#C43B71',
                          marginBottom: 4,
                        }}
                      >
                        Choose a photo or X-ray
                      </div>
                      <div
                        style={{
                          fontFamily: 'var(--font-body), Manrope, system-ui',
                          fontSize: 13,
                          color: '#5D6472',
                        }}
                      >
                        JPG or PNG, up to 5 MB
                      </div>
                    </>
                  )}
                </div>
              </div>

              <p
                style={{
                  fontFamily: 'var(--font-body), Manrope, system-ui',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: '#6B7280',
                  margin: 0,
                }}
              >
                A first look, not a diagnosis. Your dentist confirms everything.
              </p>

              <button type="submit" className="primary-btn" disabled={busy}>
                {busy ? 'Looking at your photo...' : 'Start detection'}
              </button>
            </div>
          </div>

          {phase.name === 'submitting' || phase.name === 'pending' ? (
            <div
              role="status"
              aria-busy="true"
              style={{
                fontFamily: 'var(--font-body), Manrope, system-ui',
                fontSize: 14,
                color: '#1A1A2E',
                marginTop: '16px',
              }}
            >
              Analyzing your image…
            </div>
          ) : null}

          {phase.name === 'gave-up' ? (
            <div
              role="status"
              style={{
                padding: '16px',
                borderRadius: '12px',
                background: '#FFF8EC',
                border: '1px solid #B7791F',
                color: '#1A1A2E',
                marginTop: '16px',
              }}
            >
              <p style={{margin: '0 0 12px', fontSize: '14px'}}>{STILL_PROCESSING_MESSAGE}</p>
              <button
                type="button"
                className="primary-btn"
                onClick={() => checkAgain(phase.jobId, phase.type, phase.imageUrl)}
              >
                Check again
              </button>
            </div>
          ) : null}

          {phase.name === 'failed' && (
            <div
              role="alert"
              style={{
                padding: '16px',
                borderRadius: '12px',
                background: '#FFF0F5',
                border: '1px solid #C43B71',
                color: '#1A1A2E',
                marginTop: '16px',
              }}
            >
              <h4 style={{margin: '0 0 4px', fontSize: '15px', fontWeight: 600}}>Analysis failed</h4>
              <p style={{margin: '0 0 12px', fontSize: '14px'}}>{phase.message}</p>
              <button type="button" className="primary-btn" onClick={start}>
                Retry
              </button>
            </div>
          )}
        </form>
      ) : (
        <div className="dental-ai-container">
          <div>
            <h2 className="result-title">{userResult.summary}</h2>
            <p className="dental-ai-subtitle" style={{marginTop: 8}}>
              See below for details and next steps.
            </p>
          </div>

          <div
            style={{
              borderRadius: 20,
              overflow: 'hidden',
              background: '#f0f0f0',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              position: 'relative',
              width: '100%',
              aspectRatio: `${userResult.naturalWidth} / ${userResult.naturalHeight}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={userResult.imageUrl}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              viewBox={`0 0 100 100`}
              preserveAspectRatio="none"
            >
              {userResult.boxes.map((box, idx) => (
                <g key={box.id}>
                  <rect
                    x={box.x * 100}
                    y={box.y * 100}
                    width={box.w * 100}
                    height={box.h * 100}
                    fill="none"
                    stroke="#C43B71"
                    strokeWidth="0.8"
                  />
                  <circle cx={box.x * 100} cy={box.y * 100} r="2.5" fill="#C43B71" />
                  <text
                    x={box.x * 100}
                    y={box.y * 100}
                    fontSize="3"
                    fontWeight="700"
                    fill="#fff"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontFamily="var(--font-display), Manrope, system-ui"
                  >
                    {idx + 1}
                  </text>
                </g>
              ))}
            </svg>
          </div>

          <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
            {userResult.boxes.map((box, idx) => (
              <div key={box.id} style={{display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0'}}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                    background: '#C43B71',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-display), Manrope, system-ui',
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#fff',
                    }}
                  >
                    {idx + 1}
                  </span>
                </div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div
                    style={{
                      fontFamily: 'var(--font-body), Manrope, system-ui',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#1A1A2E',
                    }}
                  >
                    {box.label}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--font-body), Manrope, system-ui',
                      fontSize: 13,
                      color: '#6B7280',
                    }}
                  >
                    {box.confidence}% confidence
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="result-disclaimer">
            <p
              style={{
                fontFamily: 'var(--font-body), Manrope, system-ui',
                fontSize: 14,
                lineHeight: 1.5,
                color: '#6B7280',
                margin: '0 0 10px',
              }}
            >
              This is a first look, not a diagnosis. Causes vary by person, so bring this to your dentist. If
              something hurts, see them now.
            </p>
            <details style={{cursor: 'pointer'}}>
              <summary
                style={{
                  fontFamily: 'var(--font-body), Manrope, system-ui',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#C43B71',
                  outline: 'none',
                  userSelect: 'none',
                }}
              >
                What this can and can&apos;t tell you
              </summary>
              <p
                style={{
                  fontFamily: 'var(--font-body), Manrope, system-ui',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: '#6B7280',
                  margin: '10px 0 0',
                }}
              >
                This tool uses AI to identify areas that might need attention based on the image you provided.
                It is not a diagnosis. A dentist must evaluate the findings in person, considering your full
                dental history, symptoms, and other factors.
              </p>
            </details>
          </div>

          <button className="secondary-btn" onClick={handleTryAnother}>
            Check another photo
          </button>
        </div>
      )}
    </PageShell>
  );
}
